// src/pages/CompleteSignupPage.js
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { http } from '../lib/axios';
import { login } from '../api/auth';
import { persistOAuthSession, routeForRole } from '../lib/authSession';
import ContextHelpButton from '../components/ContextHelpButton';

export default function CompleteSignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!uid || !token) {
      setError('Missing signup parameters. Please check your link.');
      return;
    }
    if (!password || !password2) {
      setError('Please fill out both password fields.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await http.post('/api/accounts/complete-signup/', {
        uid,
        token,
        password,
        password2,
      });

      const username = data?.username;
      const role = data?.role;

      if (!username) {
        throw new Error('Signup completed, but login details were not returned.');
      }

      const tokenResponse = await login(username, password);
      persistOAuthSession(tokenResponse?.data, role);

      setSuccessMsg(data?.message || 'Password set successfully. Signing you in now.');
      setTimeout(() => navigate(routeForRole(role)), 1200);
    } catch (err) {
      const resp = err?.response?.data;
      if (resp) {
        // Simple way to surface DRF errors
        if (typeof resp === 'string') {
          setError(resp);
        } else if (resp.detail) {
          setError(resp.detail);
        } else {
          // Combine messages
          const msg = Object.entries(resp)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
            .join(' | ');
          setError(msg);
        }
      } else {
        setError('Failed to complete signup. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 0 }}>
            Complete Your Signup
          </Typography>
          <ContextHelpButton section="getting-started" title="Help for completing signup" />
        </Box>

        {!uid || !token ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            This link is missing required information. Please contact ARTflow support or request a new invite.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please choose a password to activate your ARTflow account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {successMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMsg}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                margin="normal"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
              />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{ minWidth: 160 }}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Set Password'
                  )}
                </Button>
              </Box>
            </form>
          </>
        )}
      </Box>
    </Container>
  );
}
